import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import Faq, { faqSchema } from "./UI/Faq";
import CtaBand from "./UI/CtaBand";
import Icon from "./UI/Icon";
import { getService } from "../data/services";
import { SITE } from "../data/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/carpentry-services" replace />;

  const url = `${SITE.url}/carpentry-services/${service.slug}/`;
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Carpentry Services", to: "/carpentry-services" },
    { label: service.navLabel },
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.navLabel,
    serviceType: service.navLabel,
    description: service.metaDescription,
    url,
    areaServed: { "@type": "AdministrativeArea", name: "Cambridgeshire" },
    provider: { "@id": `${SITE.url}/#business` },
  };

  const related = (service.related || [])
    .map((s) => getService(s))
    .filter(Boolean);

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        canonical={url}
        type="article"
        image={service.image}
        schema={[
          serviceSchema,
          faqSchema(service.faqs),
          breadcrumbSchema(crumbs),
        ]}
      />

      <PageHero
        eyebrow={service.eyebrow}
        title={service.heroTitle}
        intro={service.heroIntro}
        image={service.image}
        crumbs={<Breadcrumbs items={crumbs} />}
        actions={
          <>
            <Link to="/contact" className="btn-primary">Get a free quote</Link>
            <a href={SITE.phoneHref} className="btn-outline-light">
              <Icon name="phone" className="w-4 h-4" /> {SITE.phone}
            </a>
          </>
        }
      />

      {/* Overview */}
      <section className="max-w-site mx-auto px-5 sm:px-10 py-20 md:py-28">
        <div className="lg:grid lg:grid-cols-[1.6fr_1fr] lg:gap-16 items-start">
          <div className="prose-links">
            <SectionHeading eyebrow="Overview" title={`${service.heroTitle} in Cambridge`} />
            <div className="mt-6 space-y-5 text-bark text-lg leading-8 max-w-none">
              {service.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p>
                {service.closingPara} Explore all of our{" "}
                <Link to="/carpentry-services">carpentry services</Link>, see{" "}
                <Link to="/our-work">recent projects</Link>, or{" "}
                <Link to="/contact">request a free quote</Link>.
              </p>
            </div>
          </div>

          <aside className="mt-10 lg:mt-0 bg-linen border border-hairline p-7 lg:sticky lg:top-24">
            {service.priceNote && (
              <div className="mb-6 pb-6 border-b border-hairline">
                <h3 className="eyebrow mb-3">Rough pricing</h3>
                <p className="text-ink font-display text-lg leading-7">{service.priceNote}</p>
              </div>
            )}
            <h3 className="font-display font-semibold text-2xl text-ink">Free, no-obligation quote</h3>
            <p className="text-bark leading-7 mt-3">
              Tell us about your project and we’ll arrange a visit and a clear written quote.
            </p>
            <div className="mt-6 space-y-3">
              <a href={SITE.phoneHref} className="btn-dark w-full">
                <Icon name="phone" className="w-4 h-4" /> {SITE.phone}
              </a>
              <Link to="/contact" className="btn-outline w-full">
                Send us a message
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* What we do */}
      <section className="bg-linen border-y border-hairline">
        <div className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
          <SectionHeading eyebrow="What we do" title="How we can help" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {service.features.map((f) => (
              <div key={f.title} className="bg-paper border border-hairline p-6 hover:border-oak transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-oak/10 text-oak mb-4">
                  <Icon name={service.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-xl text-ink mb-2">{f.title}</h3>
                <p className="text-bark leading-7">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
        <div className="lg:grid lg:grid-cols-[1fr_1.6fr] lg:gap-16 items-start">
          <div className="mb-8 lg:mb-0">
            <SectionHeading eyebrow="Questions" title="Frequently asked" />
          </div>
          <Faq items={service.faqs} />
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="bg-linen border-t border-hairline">
          <div className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
            <SectionHeading eyebrow="Related" title="Other things we build" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/carpentry-services/${r.slug}`}
                  className="group bg-paper border border-hairline overflow-hidden hover:border-oak transition-colors"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.cardTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-semibold text-xl text-ink group-hover:text-oak transition-colors">
                      {r.cardTitle}
                    </h3>
                    <p className="text-bark leading-7 mt-2">{r.cardBlurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        title={`Thinking about ${service.heroTitle.toLowerCase()}?`}
        text={service.ctaText}
      />
    </>
  );
}
